import React, { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { useApp } from "../../context/AppContext";

// 仅确定本次变动的接收人；业务记录的实际交接要另行处理。
export function TransitionHandoverManager({ transition, zh }) {
  const { api } = useApp();
  const T = (cn, en) => (zh ? cn : en);
  const [data, setData] = useState(null);
  const [branches, setBranches] = useState([]);
  const [choice, setChoice] = useState("");
  const [reason, setReason] = useState("");
  const [filter, setFilter] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let active = true;
    api.get("/branches").then(({ data: rows }) => { if (active) setBranches(rows || []); }).catch(() => {});
    return () => { active = false; };
  }, [api]);

  useEffect(() => {
    let active = true;
    setData(null);
    setError("");
    api.get(`/hrms/transitions/${encodeURIComponent(transition.id)}/handover-manager`)
      .then(({ data: result }) => {
        if (!active) return;
        setData(result);
        setChoice((result.selectedManager?.available ? result.selectedManager.employeeId : "") || (result.defaultManager?.status === "available" ? result.defaultManager.employeeId : ""));
        setReason(result.selectedManager?.reason || "");
      })
      .catch((err) => { if (active) setError(err?.response?.data?.detail || (zh ? "无法读取本次交接接收人" : "Could not load the handover recipient")); });
    return () => { active = false; };
  }, [api, transition.id, reload, zh]);

  const save = useCallback(async () => {
    if (!data || !choice) return;
    setBusy(true);
    setError("");
    try {
      await api.put(`/hrms/transitions/${encodeURIComponent(transition.id)}/handover-manager`,
        { employeeId: choice, reason, version: data.version });
      setReload((n) => n + 1);
    } catch (err) {
      setError(err?.response?.data?.detail || (zh ? "保存失败，请重试" : "Could not save; please try again"));
    } finally {
      setBusy(false);
    }
  }, [api, choice, data, reason, transition.id, zh]);

  const branchName = (id) => {
    const branch = branches.find((b) => b.id === id);
    return branch ? (zh ? branch.zh : branch.en || branch.zh) : id;
  };
  const source = data?.sourceOrg || {};
  const defaultManager = data?.defaultManager || {};
  const selected = data?.selectedManager;
  const defaultId = defaultManager.status === "available" ? defaultManager.employeeId : "";
  const needsReason = !!choice && choice !== defaultId;
  const text = filter.trim().toLowerCase();
  const candidates = (data?.candidates || []).filter((c) => c.employeeId === choice || !text ||
    `${c.name} ${c.username} ${c.branch} ${c.departmentName} ${c.departmentNameEn}`.toLowerCase().includes(text));
  const defaultText = defaultManager.status === "available" ? `${defaultManager.name} (${defaultManager.username})`
    : defaultManager.status === "departing" ? T("配置的经理是本次变动员工", "The configured manager is the departing employee")
    : defaultManager.status === "unavailable" ? T("配置的经理当前不可用", "The configured manager is unavailable")
    : T("尚未配置或无法确定", "Not configured or unavailable");

  return (
    <section className="space-y-2 rounded-lg border border-border bg-muted/30 p-3 text-xs" data-testid={`hrms-transition-manager-${transition.id}`}>
      <h3 className="font-semibold text-foreground">{T("本次交接接收人", "Recipient for this handover")}</h3>
      {!data && !error && <p className="text-muted-foreground" role="status"><Loader2 className="mr-1 inline h-3.5 w-3.5 animate-spin" />{T("正在读取", "Loading")}</p>}
      {data && (
        <>
          {defaultManager.status === "source_missing" ? (
            <p className="text-amber-800">{T("旧流程没有保存变动前的分公司和部门，请先核对原组织，不能自动确定接收人。", "This older workflow has no original branch and department on record. Confirm the original organization before selecting a recipient.")}</p>
          ) : (
            <>
              <p className="text-muted-foreground">{T("变动前组织", "Original organization")}: {branchName(source.branch)} · {(zh ? source.departmentName : source.departmentNameEn) || source.department}</p>
              <p className="text-muted-foreground">{T("部门默认经理", "Department default manager")}: {defaultText}</p>
              <p className="text-muted-foreground">{T("本次已选", "Selected for this handover")}: {selected ? `${selected.name} (${selected.username})${selected.available ? "" : T(" · 已不可用，请重新选择", " · unavailable; choose another")}` : T("尚未确定", "Not selected")}</p>
              {selected?.reason && <p className="text-muted-foreground">{T("例外原因", "Exception reason")}: {selected.reason}</p>}
              {data.status === "open" && <div className="space-y-2 border-t border-border pt-2">
                <label className="block text-muted-foreground">{T("搜索接收人", "Search recipients")}
                  <input value={filter} onChange={(ev) => setFilter(ev.target.value)} className="mt-1 w-full rounded-md border border-input bg-background px-2 py-1.5 text-foreground focus-visible:ring-2 focus-visible:ring-brand-400" />
                </label>
                <label className="block text-muted-foreground">{T("选择在职且账号启用的接收人", "Select an active employee with an enabled account")}
                  <select value={choice} onChange={(ev) => { setChoice(ev.target.value); setReason(""); }} className="mt-1 w-full rounded-md border border-input bg-background px-2 py-1.5 text-foreground focus-visible:ring-2 focus-visible:ring-brand-400">
                    <option value="">{T("请选择", "Select")}</option>
                    {candidates.map((c) => <option key={c.employeeId} value={c.employeeId}>{c.name} ({c.username}) · {branchName(c.branch)} · {(zh ? c.departmentName : c.departmentNameEn) || c.department}</option>)}
                  </select>
                </label>
                {needsReason && <label className="block text-muted-foreground">{T("例外原因（必填）", "Reason for exception (required)")}
                  <textarea value={reason} maxLength={500} onChange={(ev) => setReason(ev.target.value)} rows={2} className="mt-1 w-full rounded-md border border-input bg-background px-2 py-1.5 text-foreground focus-visible:ring-2 focus-visible:ring-brand-400" />
                </label>}
                <button type="button" onClick={save} disabled={busy || !choice || (needsReason && !reason.trim())} className="rounded-md bg-primary px-3 py-1.5 text-primary-foreground disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-brand-400">
                  {busy ? T("保存中", "Saving") : T("保存接收人", "Save recipient")}
                </button>
              </div>}
            </>
          )}
          <p className="text-muted-foreground">{T("这里只记录接收人；客户、项目、账号和业绩尚未因此变更。", "This records the recipient only; customers, projects, accounts, and sales credit are not changed here.")}</p>
        </>
      )}
      {error && <p role="alert" className="text-destructive">{error}{" "}<button type="button" onClick={() => setReload((n) => n + 1)} className="underline focus-visible:ring-2 focus-visible:ring-brand-400">{T("重新加载", "Reload")}</button></p>}
    </section>
  );
}
